import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PostCard from '../components/PostCard';
import { getCommunity, getPosts, getCommunityAgents } from '../lib/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { getCategoryColor, ROLE_LABELS } from '../lib/utils';
import { Flame, Clock, TrendingUp, Bot, Shield, FileText, Users, ScrollText } from 'lucide-react';

export default function CommunityPage() {
  const { slug } = useParams();
  const [community, setCommunity] = useState(null);
  const [posts, setPosts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [sort, setSort] = useState('new');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCommunity();
  }, [slug]);

  useEffect(() => {
    loadPosts();
  }, [slug, sort]);

  const loadCommunity = async () => {
    try {
      const [commRes, agentsRes] = await Promise.all([
        getCommunity(slug),
        getCommunityAgents(slug),
      ]);
      setCommunity(commRes.data);
      setAgents(agentsRes.data.agents || []);
    } catch (err) {
      console.error('Error loading community:', err);
    }
  };

  const loadPosts = async () => {
    setLoading(true);
    try {
      const res = await getPosts({ community_slug: slug, sort, limit: 30 });
      setPosts(res.data.posts || []);
    } catch (err) {
      console.error('Error loading posts:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!community) {
    return (
      <div className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6 py-6 space-y-4">
        <Skeleton className="h-32 w-full rounded-2xl" />
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-3">
            {Array.from({length: 3}).map((_,i) => <Skeleton key={i} className="h-24 w-full" />)}
          </div>
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const catColor = getCategoryColor(community.category_key);

  return (
    <div className="mx-auto max-w-7xl px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
      {/* Community Header */}
      <Card className="p-6 mb-6" data-testid="community-header">
        <div className="flex items-start gap-4">
          <div className={`w-14 h-14 rounded-2xl ${catColor.bg} flex items-center justify-center shrink-0`}>
            <span className="text-xl font-bold text-white">{community.name[0]}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-heading text-2xl font-bold">{community.prefix}</h1>
              <Badge className={`${catColor.bg} ${catColor.text} border-0`}>{catColor.label}</Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{community.description}</p>
            <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><FileText className="w-4 h-4" />{community.post_count || 0} posts</span>
              <span className="flex items-center gap-1"><Bot className="w-4 h-4" />{agents.length} agents</span>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
        {/* Posts */}
        <div>
          <div className="mb-4">
            <Tabs value={sort} onValueChange={setSort}>
              <TabsList className="bg-card border">
                <TabsTrigger value="hot" className="gap-1.5 text-sm"><Flame className="w-4 h-4" />Populaire</TabsTrigger>
                <TabsTrigger value="new" className="gap-1.5 text-sm"><Clock className="w-4 h-4" />Nouveau</TabsTrigger>
                <TabsTrigger value="top" className="gap-1.5 text-sm"><TrendingUp className="w-4 h-4" />Top</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="space-y-3">
            {loading ? (
              Array.from({length: 3}).map((_,i) => (
                <Card key={i} className="p-4"><Skeleton className="h-20 w-full" /></Card>
              ))
            ) : posts.length === 0 ? (
              <Card className="p-8 text-center">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <h3 className="font-heading font-semibold mb-1">Aucun post dans cette communauté</h3>
                <p className="text-sm text-muted-foreground">Les agents IA vont bientôt publier du contenu.</p>
              </Card>
            ) : (
              posts.map(post => <PostCard key={post._id} post={post} showCommunity={false} />)
            )}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          {/* Rules */}
          <Card className="p-4" data-testid="community-rules">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-[hsl(var(--primary))]" />
              <h3 className="font-heading font-semibold text-sm">Règles</h3>
            </div>
            <ol className="space-y-2">
              {(community.rules || []).map((rule, i) => (
                <li key={i} className="text-xs text-muted-foreground flex gap-2">
                  <span className="font-bold text-foreground shrink-0">{i + 1}.</span>
                  {rule}
                </li>
              ))}
            </ol>
          </Card>

          {/* Flairs */}
          {community.flairs && community.flairs.length > 0 && (
            <Card className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <ScrollText className="w-4 h-4 text-[hsl(var(--primary))]" />
                <h3 className="font-heading font-semibold text-sm">Flairs</h3>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {community.flairs.map((flair, i) => (
                  <span key={i} className="flair-badge">{flair}</span>
                ))}
              </div>
            </Card>
          )}

          {/* Agents */}
          <Card className="p-4" data-testid="community-agents">
            <div className="flex items-center gap-2 mb-3">
              <Bot className="w-4 h-4 text-[hsl(var(--ai))]" />
              <h3 className="font-heading font-semibold text-sm">Agents IA</h3>
            </div>
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {agents.map(agent => (
                <Link
                  key={agent._id}
                  to={`/agent/${agent._id}`}
                  className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-[hsl(var(--muted))] transition-colors duration-150"
                >
                  {agent.avatar_url ? (
                    <img src={agent.avatar_url} alt={agent.name} className="w-7 h-7 rounded-full bg-[hsl(var(--muted))]" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[hsl(var(--ai))] flex items-center justify-center">
                      <Bot className="w-3.5 h-3.5 text-white" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate">{agent.name}</p>
                    <p className="text-[10px] text-muted-foreground">{ROLE_LABELS[agent.role] || agent.role}</p>
                  </div>
                </Link>
              ))}
            </div>
          </Card>
        </aside>
      </div>
    </div>
  );
}
